import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { siteConfig } from "@/lib/siteConfig";
import { useCalendly } from "@/components/CalendlyContext";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openCalendly } = useCalendly();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
  }, [mobileOpen]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-[80] transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]" : "bg-transparent"
        }`}
      >
        <div className="container-custom flex items-center justify-between h-20">
          <Link data-testid="navbar-logo-link" to="/" className="flex items-center gap-3 group" onClick={() => setMobileOpen(false)}>
            <img src={siteConfig.logoUrl} alt="DULORME LABS logo" className="h-9 w-auto transition-transform duration-500 group-hover:rotate-[8deg]" />
            <span className="font-display font-bold text-lg tracking-tight text-white hidden sm:block">
              DULORME LABS<sup className="text-[9px] ml-0.5">™</sup>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {siteConfig.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`navbar-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-sm tracking-wide font-medium transition-colors duration-300 py-2 ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {item.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-px bg-violet-400 transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="navbar-book-call-btn"
              onClick={openCalendly}
              className="hidden lg:inline-flex items-center bg-white text-black text-xs font-bold tracking-widest uppercase px-6 py-3 hover:bg-violet-500 hover:text-white transition-colors duration-300"
            >
              Book A Strategy Call
            </button>
            <button
              data-testid="navbar-mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden text-white p-2"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[75] bg-[#050505] lg:hidden pt-28 px-6"
            data-testid="mobile-menu-overlay"
          >
            <nav className="flex flex-col gap-2">
              {siteConfig.nav.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-navbar-link-${item.label.toLowerCase()}`}
                    className="block py-4 border-b border-white/10 font-display text-3xl font-light text-white"
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <button
              data-testid="mobile-navbar-book-call-btn"
              onClick={() => {
                setMobileOpen(false);
                openCalendly();
              }}
              className="mt-10 w-full bg-white text-black text-sm font-bold tracking-widest uppercase px-6 py-4 hover:bg-violet-500 hover:text-white transition-colors duration-300"
            >
              Book A Strategy Call
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
